import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface IgCompetitor {
  username: string;
  display_name: string | null;
  category: string | null;
  is_us: boolean;
  notes: string | null;
  active: boolean;
  added_at: string;
}

export interface IgCompetitorSnapshot {
  username: string;
  snapshot_date: string;
  followers_count: number | null;
  follows_count: number | null;
  media_count: number | null;
  biography: string | null;
  profile_picture_url: string | null;
  is_verified: boolean | null;
  website: string | null;
}

export interface IgCompetitorPost {
  post_id: string;
  username: string;
  posted_at: string | null;
  media_type: string | null;
  caption: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  r2_thumb_key: string | null;
  r2_media_key: string | null;
  like_count: number | null;
  comments_count: number | null;
  views: number | null;
  is_boosted: boolean | null;
}

export function useIgCompetitors() {
  return useQuery({
    queryKey: ['ig-competitors'],
    queryFn: async (): Promise<IgCompetitor[]> => {
      const { data, error } = await marketing
        .from('ig_competitors')
        .select('*')
        .eq('active', true)
        .order('is_us', { ascending: false })
        .order('username');
      if (error) throw error;
      return (data ?? []) as unknown as IgCompetitor[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useIgCompetitorSnapshots() {
  return useQuery({
    queryKey: ['ig-competitor-snapshots'],
    queryFn: async (): Promise<IgCompetitorSnapshot[]> => {
      const { data, error } = await marketing
        .from('ig_competitor_snapshot')
        .select('*')
        .order('snapshot_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as IgCompetitorSnapshot[];
    },
    staleTime: 5 * 60_000,
  });
}

/** Counts of posts per username — used for sync status display. */
export function useIgCompetitorPostCounts() {
  return useQuery({
    queryKey: ['ig-competitor-post-counts'],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await marketing
        .from('ig_competitor_posts')
        .select('username');
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of (data ?? []) as unknown as { username: string }[]) {
        counts[row.username] = (counts[row.username] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 60_000,
  });
}

export function useIgCompetitorPosts(username?: string, limit = 12) {
  return useQuery({
    queryKey: ['ig-competitor-posts', username ?? 'all', limit],
    queryFn: async (): Promise<IgCompetitorPost[]> => {
      let q = marketing
        .from('ig_competitor_posts')
        .select('*')
        .order('like_count', { ascending: false })
        .limit(limit);
      if (username) q = q.eq('username', username);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as IgCompetitorPost[];
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Recent posts across all (or one) competitors, filtered to the last
 * N days and sorted newest-first. Different lens from useIgCompetitorPosts
 * (which is engagement-sorted) — this one answers "what's been happening
 * lately?" rather than "what worked best ever?".
 */
export function useIgCompetitorPostsRecent(
  daysWindow = 10,
  username?: string,
  limit = 200,
) {
  return useQuery({
    queryKey: ['ig-competitor-posts-recent', username ?? 'all', daysWindow, limit],
    queryFn: async (): Promise<IgCompetitorPost[]> => {
      const since = new Date(Date.now() - daysWindow * 86400000).toISOString();
      let q = marketing
        .from('ig_competitor_posts')
        .select('*')
        .gte('posted_at', since)
        .order('posted_at', { ascending: false })
        .limit(limit);
      if (username) q = q.eq('username', username);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as IgCompetitorPost[];
    },
    staleTime: 5 * 60_000,
  });
}
